import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "recharts";
import colors from "tailwindcss/colors";
import { useSuspenseGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import { allSupplierDashboardBookingStatsQuery } from "@/app/Dashboard/GraphQL/allSupplierDashboardBookingStatsQuery";
import { ComparisonMode } from "@/gql/graphql";
import { toDateTimeTzString } from "@/lib/Formatters/toDateTimeString";
import { TrendIndicator } from "@/app/Dashboard/Components/Blocks/TrendIndicator";
import { useTranslations } from "use-intl";

const integerNumberFormatter = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 0,
});

export function AllSupplierBookingStatusBarChartBlock({
  start,
  end,
  comparisonMode,
}: {
  start: string;
  end: string;
  comparisonMode: ComparisonMode;
}) {
  const t = useTranslations();
  const { data } = useSuspenseGqlQuery(allSupplierDashboardBookingStatsQuery, {
    start: toDateTimeTzString(start)!,
    end: toDateTimeTzString(end)!,
    comparisonMode,
  });

  const dashboardStats = data.allSupplierDashboardBookingStats[0];
  const suppliers = dashboardStats?.suppliers ?? [];

  const chartData = suppliers.map((stat) => ({
    supplier: stat.supplier_name,
    pending: stat.pending_count,
    confirmed: stat.confirmed_count,
    completed: stat.completed_count,
    cancelled: stat.cancelled_count,
  }));

  const avgConfirmationHours = (() => {
    const validHours = suppliers
      .map((s) => s.avg_confirmation_hours)
      .filter((h) => h !== null && h !== undefined) as number[];

    if (validHours.length === 0) return null;

    return validHours.reduce((sum, h) => sum + h, 0) / validHours.length;
  })();

  const getConfirmationTimePosition = (hours: number | null | undefined) => {
    if (hours === null || hours === undefined) return 0;
    if (hours <= 4) return 0;
    if (hours >= 48) return 100;
    return ((hours - 4) / (48 - 4)) * 100;
  };

  const chartConfig = {
    pending: {
      label: t("dashboard.chartLabels.pending"),
      color: colors.yellow[500],
    },
    confirmed: {
      label: t("dashboard.chartLabels.confirmed"),
      color: colors.green[500],
    },
    completed: {
      label: t("dashboard.chartLabels.completed"),
      color: colors.blue[500],
    },
    cancelled: {
      label: t("dashboard.chartLabels.cancelled"),
      color: colors.red[500],
    },
  } satisfies ChartConfig;

  const successfulBookings =
    dashboardStats?.thisPeriod.successful_bookings_count ?? 0;
  const comparisonSuccessfulBookings =
    dashboardStats?.lastPeriod.successful_bookings_count ?? 0;

  const trend =
    comparisonSuccessfulBookings > 0
      ? ((successfulBookings - comparisonSuccessfulBookings) /
          comparisonSuccessfulBookings) *
        100
      : null;

  return (
    <Card className="flex flex-col">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>{t("dashboard.blocks.bookings.title")}</CardTitle>
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <span>
              {successfulBookings}{" "}
              {t("dashboard.blocks.bookings.successfulBookings")}
            </span>
            {trend ? (
              <TrendIndicator
                trend={trend}
                currentValue={successfulBookings}
                previousValue={comparisonSuccessfulBookings}
                startDate={start}
                endDate={end}
                comparisonMode={comparisonMode}
                formatValue={(value) =>
                  integerNumberFormatter.format(Math.round(value))
                }
              />
            ) : null}
          </div>
        </div>
      </CardHeader>
      <CardContent className="flex flex-1 flex-col">
        {suppliers.length > 0 ? (
          <>
            <ChartContainer config={chartConfig} className="w-full flex-1">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ left: -20, right: 12 }}>
                  <CartesianGrid vertical={false} />
                  <XAxis
                    dataKey="supplier"
                    tickLine={false}
                    axisLine={false}
                    tickMargin={10}
                  />
                  <YAxis axisLine={false} tickSize={10} />
                  <ChartTooltip content={<ChartTooltipContent />} />
                  <Bar
                    dataKey="pending"
                    fill={colors.yellow[500]}
                    radius={[0, 0, 0, 0]}
                    stackId="a"
                  />
                  <Bar
                    dataKey="confirmed"
                    fill={colors.green[500]}
                    radius={[0, 0, 0, 0]}
                    stackId="a"
                  />
                  <Bar
                    dataKey="completed"
                    fill={colors.blue[500]}
                    radius={[0, 0, 0, 0]}
                    stackId="a"
                  />
                  <Bar
                    dataKey="cancelled"
                    fill={colors.red[500]}
                    radius={[4, 4, 0, 0]}
                    stackId="a"
                  />
                </BarChart>
              </ResponsiveContainer>
            </ChartContainer>
            {avgConfirmationHours !== null ? (
              <div className="mt-4">
                <div className="mb-2 flex items-center justify-between text-sm">
                  <span className="font-medium text-gray-700">
                    {t("dashboard.blocks.bookings.avgConfirmationTime")}
                  </span>
                  <span className="font-semibold text-gray-900">
                    {avgConfirmationHours.toFixed(1)}h
                  </span>
                </div>
                <div className="relative h-2 w-full overflow-hidden rounded-full bg-gradient-to-r from-green-500 via-yellow-500 to-red-500">
                  <div
                    className="absolute top-0 h-4 w-1 -translate-x-1/2 rounded-full bg-white shadow-md ring-2 ring-gray-900"
                    style={{
                      left: `${getConfirmationTimePosition(avgConfirmationHours)}%`,
                    }}
                  />
                </div>
                <div className="mt-1 flex justify-between text-xs text-gray-500">
                  <span>{t("dashboard.blocks.bookings.confirmationGood")}</span>
                  <span>{t("dashboard.blocks.bookings.confirmationPoor")}</span>
                </div>
              </div>
            ) : null}
          </>
        ) : (
          <div className="flex h-full items-center justify-center text-gray-400">
            {t("dashboard.blocks.bookings.noData")}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
